import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { CheckCircle2, UserPlus, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { tempGuestAPI } from '../../services/api';

const ENTITY_SCOPED_MODULES = ['ropa_intake', 'breach_intake', 'dpia_intake', 'prescreen'];

// readiness_assessment uses token links (external parties — no guest auth needed).
// questionnaire uses the Campaign creation page (bulk invite flow).
const MODULE_LABELS = {
  ropa_intake: 'ROPA Intake',
  breach_intake: 'Breach Intake',
  dpia_intake: 'DPIA Intake',
  prescreen: 'Pre-Screen Assessment',
};

/**
 * CreateTempGuestDialog
 * Reusable modal for DPOs to create a temp guest account for any module.
 *
 * Props:
 *   open: bool
 *   onOpenChange: fn
 *   defaultModule: string (pre-selected module key, e.g. "ropa_intake")
 *   onCreated: fn(guest) — called after successful creation
 */
export function CreateTempGuestDialog({ open, onOpenChange, defaultModule, onCreated, entities = [], lockedEntityId = null }) {
  const [step, setStep] = useState('form'); // 'form' | 'success'
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: '',
    full_name: '',
    module: defaultModule || '',
    auth_method: 'otp',
    expires_days: '14',
    entity_id: lockedEntityId || '',
  });
  const [result, setResult] = useState(null);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleCreate = async () => {
    if (!form.email || !form.full_name || !form.module) {
      toast.error('Please fill in all required fields.');
      return;
    }
    const needsEntity = ENTITY_SCOPED_MODULES.includes(form.module);
    if (needsEntity && !form.entity_id) {
      toast.error('Please select an entity.');
      return;
    }
    setLoading(true);
    try {
      const res = await tempGuestAPI.create({
        email: form.email.trim().toLowerCase(),
        full_name: form.full_name.trim(),
        module: form.module,
        auth_method: form.auth_method,
        expires_days: parseInt(form.expires_days, 10),
        entity_id: form.entity_id || null,
      });
      setResult(res.data);
      setStep('success');
      onCreated?.(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create guest account.';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset after dialog close animation
    setTimeout(() => { setStep('form'); setResult(null); setForm({ email: '', full_name: '', module: defaultModule || '', auth_method: 'otp', expires_days: '14', entity_id: lockedEntityId || '' }); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-md flex flex-col max-h-[90vh]">
        {step === 'form' && (
          <>
            <DialogHeader className="shrink-0">
              <DialogTitle className="text-white flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-violet-400" />
                Create Temp Login
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                The recipient will receive an email with login instructions. They'll see only their assigned form.
              </DialogDescription>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 pr-1 space-y-4 pt-2">
              <div className="space-y-2">
                <Label className="text-slate-300">Recipient email <span className="text-rose-400">*</span></Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="recipient@company.com"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Recipient name <span className="text-rose-400">*</span></Label>
                <Input
                  value={form.full_name}
                  onChange={e => set('full_name', e.target.value)}
                  placeholder="Jane Smith"
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Module <span className="text-rose-400">*</span></Label>
                <Select value={form.module} onValueChange={v => set('module', v)} disabled={!!defaultModule}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue placeholder="Select module…" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {Object.entries(MODULE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key} className="text-white focus:bg-slate-700">{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {ENTITY_SCOPED_MODULES.includes(form.module) && !lockedEntityId && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Entity <span className="text-rose-400">*</span></Label>
                  <Select value={form.entity_id} onValueChange={v => set('entity_id', v)}>
                    <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                      <SelectValue placeholder="Select entity…" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {entities.map(e => (
                        <SelectItem key={e.id} value={e.id} className="text-white focus:bg-slate-700">
                          {e.entity_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-300">Access expires after</Label>
                <Select value={form.expires_days} onValueChange={v => set('expires_days', v)}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    {[7, 14, 30, 60, 90].map(d => (
                      <SelectItem key={d} value={String(d)} className="text-white focus:bg-slate-700">{d} days</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <Label className="text-slate-300">Authentication method</Label>
                <RadioGroup value={form.auth_method} onValueChange={v => set('auth_method', v)} className="space-y-2">
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.auth_method === 'otp' ? 'border-violet-500/50 bg-violet-500/5' : 'border-slate-700 hover:border-slate-600'}`}>
                    <RadioGroupItem value="otp" className="mt-0.5" />
                    <div>
                      <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                        <Shield className="h-3.5 w-3.5 text-violet-400" />
                        One-Time Password
                        <span className="text-xs text-violet-400 font-normal">(Recommended)</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">They enter their email → receive a 6-digit code each login. Best for one-off submissions.</p>
                    </div>
                  </label>
                  <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${form.auth_method === 'password' ? 'border-violet-500/50 bg-violet-500/5' : 'border-slate-700 hover:border-slate-600'}`}>
                    <RadioGroupItem value="password" className="mt-0.5" />
                    <div>
                      <div className="flex items-center gap-1.5 text-sm font-medium text-white">
                        <KeyRound className="h-3.5 w-3.5 text-slate-400" />
                        Temporary Password
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">System generates a password sent in the invite. Best for multi-session forms.</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>

            <div className="flex gap-2 pt-4 shrink-0 border-t border-slate-800">
              <Button variant="outline" onClick={handleClose} className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800">
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={loading} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white">
                {loading ? 'Creating…' : 'Create & Send Invite'}
              </Button>
            </div>
          </>
        )}

        {step === 'success' && result && (
          <>
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
                {result.message?.includes('added') ? 'Module added' : 'Invite sent'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Email</span>
                  <span className="text-white">{result.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Module added</span>
                  <span className="text-white">{MODULE_LABELS[result.module]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Auth method</span>
                  <span className="text-white capitalize">{result.auth_method === 'otp' ? 'One-Time Password' : 'Temp Password'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Expires</span>
                  <span className="text-white">{new Date(result.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                </div>
              </div>
              <p className="text-sm text-slate-400">An invite email has been sent with login instructions.</p>
              <Button onClick={handleClose} className="w-full bg-violet-600 hover:bg-violet-700 text-white">Done</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
